/*
 * Ryan O'Dowd
 * 2024-10-02
 * © Copyright 2024 Oakwood Software Consulting, Inc. All Rights Reserved.
 */
import React, {
  useState,
} from 'react';
import {
  adminAddClassroom,
  adminUpdateClassroom,
} from '@utils/store/actions';
import Button from '@components/common/Button';
import Dialog from '@components/common/Dialog';
import FormControl from '@components/common/FormControl';
import Switch from '@components/common/Switch';
import Text from '@components/common/Text';
import TextInput from '@components/common/TextInput';
import getStyles from './styles';
import hooks from '@hooks';

const NewOrEditClassroomDialog = (props: NewOrEditClassroomDialogProps) => {
  const styles = hooks.useStyles(getStyles);
  const i18n = hooks.useI18n();
  const dispatch = hooks.useTypedDispatch();

  const [_classroomName, setClassroomName] = useState(props.classroom?.name || '');
  const [_joinCode, setJoinCode] = useState(props.classroom?.join_code || '');
  const [_isJoinCodeEnabled, setIsJoinCodeEnabled] = useState(+(props.classroom?.is_join_code_enabled ?? 1));
  const [_teacherDisplayName, setTeacherDisplayName] = useState('');
  const [_teacherUserId, setTeacherUserId] = useState('');
  const [_errorMessage, setErrorMessage] = useState('');

  return (
    <Dialog
      visible={true}
      onDismiss={() => props.onClose()} // @TODO: ask about unsaved changes
    >
      <Dialog.Title>Add/edit classroom</Dialog.Title>{/* @TODO: i18n */}
      <Dialog.Content>
        <TextInput
          mode='outlined'
          value={_classroomName}
          onChangeText={(text) => setClassroomName(text)}
          label='Classroom name'
        />
        {!!_joinCode &&
          <TextInput
            mode='outlined'
            value={_joinCode}
            onChangeText={(text) => setJoinCode(text.toUpperCase())}
            // @TODO: paper equivalent? inputProps={{maxLength: 7}}
            label='Join code'
          />
        }
        <Switch
          isChecked={!!+_isJoinCodeEnabled}
          onValueChange={() => {
            setIsJoinCodeEnabled((curr) => +(!curr));
          }}
          label='Join code is enabled'
        />

        {!props.classroom && // @TODO: have separate ui for modifying teachers or reassigning teachers within a classroom
          <React.Fragment>
            <Text style={styles.errorMessage}>{"This is for new teachers only.  If they're already a classrom teacher, insert into DB manually (or build this functionaility)."}</Text>
            <FormControl.Row>
              <TextInput
                mode='outlined'
                value={_teacherDisplayName}
                onChangeText={(text) => setTeacherDisplayName(text)}
                label='Teacher display name (for students)'
              />
            </FormControl.Row>

            <FormControl.Row>
              <TextInput
                mode='outlined'
                value={_teacherUserId}
                // @TODO: paper equivalent? type='number'
                onChangeText={(text) => setTeacherUserId(text)}
                label='Teacher user ID (existing Wrinkly account)'
              />
            </FormControl.Row>
          </React.Fragment>
        }
      </Dialog.Content>
      <Dialog.Actions errorMessage={_errorMessage}>
        <Button
          mode='text'
          onPress={() => props.onClose()}
        >
          {i18n('cancel')}
        </Button>
        <Button
          onPress={() => {
            if (props.classroom) {
              dispatch(adminUpdateClassroom(
                props.classroom.id,
                {
                  name: _classroomName,
                  joinCode: _joinCode,
                  isJoinCodeEnabled: _isJoinCodeEnabled,
                },
                props.onClose,
              ));
            } else {
              if (!_classroomName || !_teacherDisplayName || !_teacherUserId) {
                setErrorMessage('Please fill out all fields.');
                return;
              }
              dispatch(adminAddClassroom(props.schoolId, _classroomName, _isJoinCodeEnabled, _teacherDisplayName, _teacherUserId, props.onClose));
            }
          }}
        >
          {i18n('save')}
        </Button>
      </Dialog.Actions>
    </Dialog>
  );
};

class NewOrEditClassroomDialogProps {
  onClose: () => void;
  schoolId: number;
  classroom: {
    id: number;
    name: string;
    join_code: string;
    is_join_code_enabled: 0 | 1; // @TODO: should this be a boolean?
  }; // @TODO: a classroom type
};

export default NewOrEditClassroomDialog;
